// The landing conversation, brought into signed-in Astra (#2945).
//
// Founder decision 2026-10-02 (variant A of the carry-over mocks): no
// consent step ("let's not add consent.. we want least friction"). After
// sign-in, if this tab still holds the open chat's conversation
// (sessionStorage, astraChatThread), it is saved as the student's first Astra
// thread, once, and the tab's copy is cleared. /astra and the drawer then
// show a notice with Start fresh (archives that thread, with an undo).
//
// Nothing is sent before sign-in: the import runs only for a signed-in,
// 13+-attested student with Astra on (the callers' gates, re-checked by the
// server). The carry itself sends nothing earlier; separately, since #3063
// the server keeps each question and answer, scrubbed, for 90 days to check
// answer quality (astra_texts.py; the Privacy Policy says so).
//
// What never carries: an under-13 conversation (the chat already clears
// it; the server refuses one too) and a crisis question with its 988 reply
// (dropped here and again on the server, forgetVisitorDraft's rule).
import { clearChatThread, loadChatThread } from './astraChatThread';
import { conciergeThreadImport } from './api';
import { track, EVENTS } from './analytics';
import { HANDOFF_KEY, PICTURE_KEY } from './astraLite';
import { storeCarriedSchools } from '../utils/schoolEntry';

// The import's idempotency key, kept beside the thread for the tab: a reload
// (or a lost response) re-sends the same key and the server hands back the
// thread it already made instead of writing a second one.
export const CARRY_KEY = 'astra_carry_key_v1';
// What the notice shows: {thread_id, questions, title, archived, dismissed}.
export const CARRY_NOTICE = 'astra_carry_notice_v1';
export const CARRY_NOTICE_EVENT = 'stratia:astra-carry-notice';
// Reasons the import will never succeed for this conversation: the tab's
// copy goes, so a reload doesn't try again.
const FINAL_REASONS = ['under_13', 'empty', 'bad_key', 'too_many', 'read_expired', 'read_mismatch'];

// The read the visitor unlocked (#3480, astra_read_unlock): the Lite session
// token, the picture fields it was signed over, and the one school. Written
// only when they press "Continue with Google to see it", for this tab only.
// The server re-checks the token against the fields and the school before it
// recomputes the full read; nothing here is trusted.
export const READ_CARRY_KEY = 'astra_read_carry_v1';

export function saveReadCarry({ token, fields, schoolId }) {
    if (typeof token !== 'string' || !fields || typeof schoolId !== 'string') return;
    ss.set(READ_CARRY_KEY, JSON.stringify({ token, fields, school_id: schoolId }));
}

export function loadReadCarry() {
    try {
        const r = JSON.parse(ss.get(READ_CARRY_KEY) || 'null');
        return r && typeof r.token === 'string' && r.fields && typeof r.school_id === 'string' ? r : null;
    } catch { return null; }
}

export const clearReadCarry = () => ss.del(READ_CARRY_KEY);

/**
 * Everything a read's sign-in hands over, in one place (#3480's unlock box
 * and the college page's "Where do I stand" card, #3885): the read itself
 * (re-checked by the server at the carry), the picture and its words (so
 * /profile adopts them and nothing is re-typed), the checked schools, and
 * the handoff that tells the Lite record this browser signed in.
 * `entry`: a college page's school entry (utils/schoolEntry), or null.
 */
export function holdReadForSignIn({ token, fields, schoolId, text, handoff = null, reads = [], entry = null }) {
    saveReadCarry({ token, fields, schoolId });
    if (entry) storeCarriedSchools(reads, entry);
    try {
        localStorage.setItem('scratchpad_draft_anonymous', text);
        localStorage.setItem(PICTURE_KEY, JSON.stringify({ text, fields }));
        if (handoff) localStorage.setItem(HANDOFF_KEY, handoff);
    } catch { /* private mode */ }
}

// The college page card's save (#3885): which page's card started this
// sign-in, for this tab only. The carry import below reads it once, so
// "signed in from the card" is counted where the read actually lands.
export const STAND_SAVE_KEY = 'college_stand_save_v1';
export function markStandSave({ slug, schoolId }) {
    if (typeof slug !== 'string' || typeof schoolId !== 'string') return;
    ss.set(STAND_SAVE_KEY, JSON.stringify({ slug, school_id: schoolId }));
}
function takeStandSave() {
    let m = null;
    try { m = JSON.parse(ss.get(STAND_SAVE_KEY) || 'null'); } catch { m = null; }
    ss.del(STAND_SAVE_KEY);
    return m && typeof m.slug === 'string' && typeof m.school_id === 'string' ? m : null;
}

const ss = {
    get(k) { try { return sessionStorage.getItem(k); } catch { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, v); } catch { /* private mode */ } },
    del(k) { try { sessionStorage.removeItem(k); } catch { /* nothing kept */ } },
};

/** What would carry from this tab, or null: the messages (a crisis question
 * and its reply already dropped), the schools the answers named, and how
 * many questions that leaves. */
export function carryPayload() {
    const kept = loadChatThread();
    const read = loadReadCarry();
    if (!kept) return read ? { messages: [], schools: [], questions: 0, read } : null;
    const messages = [];
    const t = kept.thread;
    for (let i = 0; i < t.length; i += 1) {
        const m = t[i];
        if (m.role === 'visitor') {
            const next = t[i + 1];
            if (next && next.role === 'astra' && next.kind === 'crisis') { i += 1; continue; }
            messages.push({ role: 'visitor', text: m.text });
        } else if (m.kind !== 'crisis') {
            messages.push({ role: 'astra', kind: 'answer', text: (m.answer && m.answer.text) || m.text });
        }
    }
    const questions = messages.filter((m) => m.role === 'visitor').length;
    if (!questions && !read) return null;
    const schools = [...new Set(t.filter((m) => m.kind !== 'crisis').map((m) => m.school).filter(Boolean))];
    return { messages, schools, questions, ...(read ? { read } : {}) };
}

export const hasCarry = () => Boolean(carryPayload());

function importKey() {
    const held = ss.get(CARRY_KEY);
    if (held && /^[A-Za-z0-9_-]{16,40}$/.test(held)) return held;
    const abc = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
    const bytes = new Uint8Array(22);
    try { crypto.getRandomValues(bytes); } catch { for (let i = 0; i < 22; i += 1) bytes[i] = Math.floor(Math.random() * 256); }
    const key = Array.from(bytes, (b) => abc[b % 64]).join('');
    ss.set(CARRY_KEY, key);
    return key;
}

export function readNotice() {
    try {
        const n = JSON.parse(ss.get(CARRY_NOTICE) || 'null');
        return n && typeof n.thread_id === 'string' ? n : null;
    } catch { return null; }
}

export function writeNotice(next) {
    if (next) ss.set(CARRY_NOTICE, JSON.stringify(next)); else ss.del(CARRY_NOTICE);
    try { window.dispatchEvent(new CustomEvent(CARRY_NOTICE_EVENT, { detail: next })); } catch { /* old browsers */ }
}

// One import per page life per account: the launcher and the chat both ask,
// and both get the same answer.
const runs = new Map();

/**
 * Save this tab's landing conversation to the signed-in account, once.
 * Resolves the notice ({thread_id, questions, title}) when this page life
 * imported (or re-found) a thread, else null. Never rejects.
 */
export function runCarryImport(email) {
    if (!email) return Promise.resolve(null);
    if (runs.has(email)) return runs.get(email);
    const run = (async () => {
        const payload = carryPayload();
        if (!payload) return null;
        // Only the read the card saved counts as signed in from the card.
        const stand = takeStandSave();
        const fromStand = stand && payload.read && payload.read.school_id === stand.school_id ? stand : null;
        const res = await conciergeThreadImport(email, {
            op: 'import', import_key: importKey(), messages: payload.messages, schools: payload.schools,
            ...(payload.read ? { read: payload.read } : {}),
        });
        if (fromStand) {
            track(EVENTS.COLLEGE_STAND_SAVE, { state: 'signed_in', slug: fromStand.slug, school_id: fromStand.school_id,
                read_carried: Boolean(res?.success && res.read) });
        }
        if (res?.success && res.id) {
            clearChatThread();
            clearReadCarry();
            ss.del(CARRY_KEY);
            // `questions` is the server's count when it gave one (0 for a read alone)
            const notice = { thread_id: res.id,
                questions: typeof res.questions === 'number' ? res.questions : payload.questions,
                title: res.title || null, archived: false, dismissed: false,
                ...(typeof res.read === 'string' ? { read: res.read } : {}) };
            writeNotice(notice);
            if (!res.already) {
                track(EVENTS.ASTRA_CARRY_IMPORTED, { questions: notice.questions,
                    schools_n: payload.schools.length + (res.read ? 1 : 0), ...(res.read ? { read: true } : {}) });
            }
            return notice;
        }
        const reason = typeof res?.reason === 'string' ? res.reason : 'refused';
        if (FINAL_REASONS.includes(reason)) {
            clearChatThread();
            clearReadCarry();
            ss.del(CARRY_KEY);
        }
        track(EVENTS.ASTRA_CARRY_FAILED, { reason });
        return null;
    })().catch(() => null);
    runs.set(email, run);
    return run;
}

/** Start fresh (archive) or its undo (restore) on the carried thread. */
export async function setCarryArchived(email, notice, archived) {
    const res = await conciergeThreadImport(email, { op: archived ? 'archive' : 'restore', thread_id: notice.thread_id });
    if (!res?.success) return false;
    writeNotice({ ...notice, archived });
    track(EVENTS.ASTRA_CARRY_UNDONE, { step: archived ? 'start_fresh' : 'undo', questions: notice.questions || 0 });
    return true;
}

export function dismissNotice() {
    const n = readNotice();
    if (n) writeNotice({ ...n, dismissed: true });
}

// The chat's bootstrap waits on the import, but never longer than this: a
// cold backend must not leave Astra blank (review of #2945). A late import
// still lands; the notice and the rail follow it.
export const CARRY_WAIT_MS = 8000;
export function runCarryImportWithin(email, ms = CARRY_WAIT_MS) {
    let timer;
    return Promise.race([
        runCarryImport(email),
        new Promise((resolve) => { timer = setTimeout(() => resolve(null), ms); }),
    ]).finally(() => clearTimeout(timer));
}

// Tests only: forget this page life's runs.
export function resetCarryRuns() { runs.clear(); opened.clear(); }

// The carried thread opens first once per page life, not on every mount of
// the chat (navigating away from /astra and back resumes as usual).
const opened = new Set();
export function claimCarryOpen(threadId) {
    if (!threadId || opened.has(threadId)) return false;
    opened.add(threadId);
    return true;
}
